<template>
  <div id="admins-dashboard-main">
    <el-row :gutter="32" class="info-boxes" type="flex" id="overview-information">
      <el-col>
        <section class="elsa-section boxes-group-header">
          <h2>{{ $t('classes.classes_details.overview_section.title') }}</h2>
        </section>
        <section class="elsa-section">
          <admin-overview-info v-if="!hasChildOrg" :numberOfStudents="numberOfStudents" :numberOfClasses="numberOfClasses" :numberOfTeachers="numberOfTeachers"> </admin-overview-info>
          <general-admin-overview-info v-else :numberOfStudents="numberOfStudentsGen" :numberOfClasses="numberOfGroups" :numberOfTeachers="numberOfTeamLeads" :numberOfBranches="numberOfBranches"> </general-admin-overview-info>
        </section>
      </el-col>
    </el-row>
    <charts-fragment
      :totalStudents="hasChildOrg ? numberOfStudentsGen : numberOfStudents"
      :activeStudents="activeStudents"
      :weeklyActiveStudents="weeklyActiveStudents"
      :avgPracticeTime="avgPracticeTime"
      :endDate="endDate"
      :eps="eps"
      :overview="overviewData"
      :sumActiveStudents="sumActiveStudents"
      :prevOverview="prevOverviewData"
      :organizationType="currentOrganizationType"
      :startDate="startDate"
      :lessonsTotalTimePracticed="lessonsTotalTimePracticed"
      :lessonsTimePracticed="lessonsTimePracticed"
      :studySetTimePracticed="studySetTimePracticed"
      :scope="scope"
      :timelineLabel="timelineLabel || this.$t('date_interval_picker.this_month')"
      :avg-elsa-score="avgElsaScore"
      :distributionEps="distributionEps"
      :distributionIelts="distributionIelts"
      :screen="'organization'"
      @updatedDate="setNewDate"
      @updateScope="updateScope"
      :pentagon="pentagon"
      :highestAverageScoreSkills="highestAverageScoreSkills"
      :lowestAverageScoreSkills="lowestAverageScoreSkills"
      :loadingAverageScoreSkills="loadingAverageScoreSkills"
      :lessonsPracticedMinuteGoal="15"
      :enabledAction="isAllChartDone"
    />
  </div>
</template>

<script>
import ElsaSection from '@/components/common/ElsaSection'
import AdminOverviewInfo from '@/components/dashboard/AdminOverviewInfo'
import GeneralAdminOverviewInfo from '@/components/dashboard/GeneralAdminOverviewInfo'
import ChartsFragment from '@/components/common/charts/ChartsFragment'
import { mapGetters, mapActions, mapState } from 'vuex'
import { pathOr, propOr, both, pathEq } from 'ramda'
import { format, startOfMonth, toDate } from 'date-fns'
import { warningMessage } from '@/utils/messages'
import showNoReportDataWarning from '@/components/common/mixins/noReportData'
import { locale_mapping_date } from '@/helpers/languages'
import { isSessionExpireError } from '@/utils/sessionExpireErr'

export default {
  mixins: [showNoReportDataWarning],
  data() {
    return {
      startDate: startOfMonth(Date.now()),
      endDate: toDate(Date.now()),
      scope: 'daily',
      isAllChartDone: false,
      // timelineLabel: '',
    }
  },
  components: {
    AdminOverviewInfo,
    GeneralAdminOverviewInfo,
    ChartsFragment
  },
  computed: {
    numberOfClasses() {
      return pathOr(0, ['buildPagination', 'total'])(this)
    },
    numberOfTeachers() {
      return pathOr(0, ['paginateTeachers', 'count'])(this)
    },
    numberOfStudents() {
      return pathOr(0, ['paginateStudents', 'count'])(this)
    },
    numberOfLicenses() {
      return 0
    },
    numberOfBranches() {
      return pathOr(0, ['currentOrganization', 'child','length'])(this)
    },
    numberOfTeamLeads() {
      return pathOr(0, ['generalOverviewData', 'teachers'])(this)
    },
    numberOfGroups() {
      return pathOr(0, ['generalOverviewData', 'groups'])(this)
    },
    numberOfStudentsGen() {
      return pathOr(0, ['generalOverviewData', 'learners'])(this)
    },
    timelineLabel() {
      const formatedStartDate = format(new Date(this.startDate), 'dd MMM yyyy', { locale: locale_mapping_date[this.$i18n.locale] })
      const formatedEndDate = format(new Date(this.endDate), 'dd MMM yyyy', {
        locale: locale_mapping_date[this.$i18n.locale]
      })
      return `${formatedStartDate} - ${formatedEndDate}`
    },
    ...mapGetters('organization', ['currentOrganizationType', 'currentOrganizationId','hasChildOrg','generalOverviewData','currentOrganization']),
    ...mapGetters('classes', ['buildPagination']),
    ...mapGetters('teachers', ['paginateTeachers']),
    ...mapGetters('students', ['paginateStudents']),
    ...mapState('organization', [
      'lessonsTotalTimePracticed',
      'lessonsTimePracticed',
      'studySetTimePracticed',
      'avgPracticeTime',
      'avgProgress',
      'weeklyActiveStudents',
      'activeStudents',
      'eps',
      'overviewData',
      'prevOverviewData',
      'avgElsaScore',
      'distributionEps',
      'distributionIelts',
      'pentagon',
      'highestAverageScoreSkills',
      'lowestAverageScoreSkills',
      'loadingAverageScoreSkills',
      'sumActiveStudents',
    ])
  },
  methods: {
    setNewDate(dateObject) {
      this.startDate = dateObject.startDate
      this.endDate = dateObject.endDate
      // this.timelineLabel = dateObject.timelineLabel

      return this.getAllChartDataPeriod({
        from: this.startDate,
        to: this.endDate,
        scope: this.scope
      })
    },
    updateScope(newScope) {
      this.scope = newScope
    },
    async getAllChartDataPeriod(options) {
      this.isAllChartDone = false;
      await this.getChartDataPeriod(options).catch((err) => {
        if(!isSessionExpireError(err))
          this.showNoReportDataWarning();
      })
      this.isAllChartDone = true;
    },
    getAllChartDataNoPeriod(options) {
      return this.getChartDataNoPeriod(options).catch((err) => {
        if(!isSessionExpireError(err))
          this.showNoReportDataWarning();
      })
    },
    ...mapActions('classes', ['getClasses']),
    ...mapActions('teachers', ['getTeachers']),
    ...mapActions('students', ['getStudents']),
    ...mapActions('organization', ['getChartDataPeriod', 'getChartDataNoPeriod', 'getGeneralOverview'])
  },
  async mounted() {
    try {
      this.getClasses()
      this.getTeachers()
      this.getStudents()
      this.getGeneralOverview()
      await this.getAllChartDataNoPeriod()
      await this.getAllChartDataPeriod({
        to: this.endDate,
        from: this.startDate,
        scope: this.scope
      })
    } catch {
      return warningMessage(this.$t('common.generic_error_message'))(this)
    }
  },
  watch: {
    scope(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.getAllChartDataPeriod({
          from: this.startDate,
          to: this.endDate,
          scope: newValue
        })
      }
    },
    currentOrganizationId: {
      async handler(newOrganizationId, oldOrganizationId) {
        if (newOrganizationId !== oldOrganizationId) {
          try {
            this.getClasses()
            this.getTeachers()
            this.getGeneralOverview()
            await this.getAllChartDataNoPeriod()
            await this.getAllChartDataPeriod({
              from: this.startDate,
              to: this.endDate
            })
          } catch {
            return warningMessage(this.$t('common.generic_error_message'))(this)
          }
        }
      },
    },
  }
};
</script>

<style lang="scss" scoped>
@import '../../scss/_colors.scss';

.dashboard-manage {
  height: 80%;
  ::v-deep .section-body {
    margin-top: 0;
  }

  a:not(:last-of-type) span {
    border-bottom: 1px solid #cee0ea;
  }

  a {
    span {
      font-weight: 600;
      font-size: 16px;
      text-decoration-line: underline;
      color: $blue;
      mix-blend-mode: normal;

      padding-top: 32px;
      padding-bottom: 32px;
    }
  }
}

.elsa-section {
  padding: 32px;
}

.elsa-section-1 {
  padding-left: 32px;
  padding-right: 32px;
}

#admins-dashboard-main {
  margin-left: 20px;
  margin-right: 20px;
}
// .el-row {
//   overflow: hidden;
// }

.activity-reports__inner {
  padding-left: 32px;
  padding-right: 32px;
  display: flex;
  align-items: center;
}
@media only screen and (max-width: 768px) {
  .activity-reports__inner {
    padding-left: 0px !important;
    padding-right: 0px !important;
  }
}

.boxes-group-header {
  display: flex;
  align-items: center;

  justify-content: space-between;
  padding-bottom: 0px;
  h2 {
    font-weight: 700;
    font-size: 32px;
    line-height: 48px;
  }
}

@media only screen and (max-width: 768px) {
  .boxes-group-header {
    padding-left: 0px !important;
    padding-right: 0px !important;
  }
}

#overview-information::v-deep .overview-item {
  padding-bottom: 16px;
}
</style>
